import { useCallback, useRef } from 'react';

// 预加载缓存，避免重复加载
const preloadCache = new Set<string>();

// 路由组件映射
const routeComponentMap: Record<string, () => Promise<any>> = {
  '/home': () => import('@/features/home'),
  '/assets': () => import('@/features/assets'),
  '/creation/text': () => import('@/features/generation/text'),
  '/creation/image': () => import('@/features/generation/image'),
  '/creation/video': () => import('@/features/generation/video'),
  '/ricegrain': () => import('@/features/ricegrain'),
};

/**
 * 路由预加载 Hook
 * 提供预加载路由组件的功能，优化页面跳转体验
 */
export const useRoutePreloader = () => {
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 预加载指定路由的组件
   * @param path 路由路径
   */
  const preloadRoute = useCallback((path: string) => {
    // 如果已经预加载过，直接返回
    if (preloadCache.has(path)) {
      return;
    }

    const componentLoader = routeComponentMap[path];
    if (componentLoader) {
      // 标记为已预加载，避免重复加载
      preloadCache.add(path);
      
      // 异步预加载组件
      componentLoader()
        .then(() => {
          console.log(`✅ 预加载完成: ${path}`);
        })
        .catch((error) => {
          console.warn(`❌ 预加载失败: ${path}`, error);
          // 预加载失败时从缓存中移除，允许重试
          preloadCache.delete(path);
        });
    }
  }, []);

  /**
   * 延迟预加载，在鼠标悬停时触发
   * @param path 路由路径
   * @param delay 延迟时间（毫秒），默认 200ms
   */
  const preloadRouteWithDelay = useCallback((path: string, delay: number = 200) => {
    // 清除之前的定时器
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }

    // 设置新的延迟预加载
    preloadTimeoutRef.current = setTimeout(() => {
      preloadRoute(path);
    }, delay);
  }, [preloadRoute]);

  /**
   * 取消预加载（鼠标离开时调用）
   */
  const cancelPreload = useCallback(() => {
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
      preloadTimeoutRef.current = null;
    }
  }, []);

  /**
   * 批量预加载多个路由
   * @param paths 路由路径数组
   * @param interval 每个路由之间的间隔时间（毫秒），默认 100ms
   */
  const preloadRoutes = useCallback((paths: string[], interval: number = 100) => {
    paths.forEach((path, index) => {
      setTimeout(() => {
        preloadRoute(path);
      }, index * interval);
    });
  }, [preloadRoute]);

  /**
   * 智能预加载：根据当前页面预加载相关页面
   * @param currentPath 当前路径
   */
  const smartPreload = useCallback((currentPath: string) => {
    // 根据当前页面智能预加载相关页面
    const preloadMap: Record<string, string[]> = {
      '/home': ['/creation/text', '/creation/image', '/creation/video', '/assets'],
      '/creation/text': ['/creation/image', '/home'],
      '/creation/image': ['/creation/video', '/home'],
      '/creation/video': ['/assets', '/home'],
      '/assets': ['/home'],
    };

    const routesToPreload = preloadMap[currentPath];
    if (routesToPreload) {
      // 延迟预加载，避免影响当前页面的加载
      setTimeout(() => {
        preloadRoutes(routesToPreload, 200);
      }, 1000);
    }
  }, [preloadRoutes]);

  return {
    preloadRoute,
    preloadRouteWithDelay,
    cancelPreload,
    preloadRoutes,
    smartPreload,
  };
};
